import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';
import { useState } from 'react';
import { useGetWidgetFigureQuery, usePreviewWidgetFigureQuery } from 'store/api/homeWidgetFigures';
import type { WidgetFigure } from 'types/homeWidgetFigures';
import type { HomeWidgetDefinition } from 'types/homeWidgets';
import { useSavedViewFigure } from './useSavedViewFigure';
import type { CountedView } from './widgetLink';

/** What a card draws from: one of a figure, a wait, a refusal or a failure. */
export type WidgetFigureState = {
    figure?: WidgetFigure;
    /** No figure to show yet. */
    isLoading: boolean;
    /** A figure is shown while a newer one is on its way. */
    isRefreshing: boolean;
    /** The figure is closed to the person looking: nothing to retry. */
    isForbidden: boolean;
    isError: boolean;
    /** A row count that stopped at its list's cap: the list holds more rows than the figure says. */
    isCapped: boolean;
    /** The view a saved-view figure counted, for the link that opens it. */
    view?: CountedView;
    retry: () => void;
};

/**
 * The one place a widget's figure is fetched: one request per widget, as the person looking. With an `id` it is the
 * stored widget's figure; without one it is the figure of the definition as it stands, for a widget still being
 * built. A saved-view widget's figure is the same either way: it is read from its list's own endpoints.
 */
export function useWidgetFigure(definition: HomeWidgetDefinition, id?: number): WidgetFigureState {
    // ponytail: the day is read once per mount, so a Home left open past midnight shows yesterday's until it is
    // opened again.
    const [asOf] = useState(() => format(new Date(), 'yyyy-MM-dd'));
    const catalogue = definition.source === 'CATALOGUE' ? definition : undefined;
    const stored = useGetWidgetFigureQuery(catalogue && id !== undefined ? { id, asOf } : skipToken);
    const built = usePreviewWidgetFigureQuery(
        catalogue && id === undefined ? { definition: catalogue, asOf } : skipToken
    );
    const savedView = useSavedViewFigure(definition.source === 'SAVED_VIEW' ? definition : undefined, asOf);
    const { data, error, isError, isFetching, refetch } = id === undefined ? built : stored;
    if (!catalogue) return savedView;

    const figure = isError ? undefined : data;
    return {
        figure,
        isLoading: !figure && !isError,
        isRefreshing: !!figure && isFetching,
        isForbidden: !!error && 'status' in error && error.status === 403,
        isError,
        isCapped: false,
        retry: refetch,
    };
}
